import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, ButtonBase, IconButton, Popover, Skeleton, Tooltip, Typography } from '@mui/material';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { colors } from '../../styles/theme';
import { formatMetaSyncTime } from '../metaAdsConnection';
import {
  metaAdsIconButtonSx, metaAdsMutedTextSx, metaAdsPanelHeaderIconSx, metaAdsQuietButtonSx, metaAdsSkeletonSx,
} from './metaAdsUi.styles';

export const META_SETTINGS_LABEL = 'Configuración de Meta';

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

const rowSx = {
  display: 'grid',
  gridTemplateColumns: '20px minmax(0, 1fr) auto',
  columnGap: 1,
  alignItems: 'start',
  py: 1.25,
  borderTop: '1px solid rgba(255,255,255,0.06)',
};
const rowIconSx = { fontSize: 18, mt: '1px', color: colors.textSecondary };
const lineSx = { fontSize: '13px', lineHeight: '18px', color: colors.textPanelTitle, fontWeight: 600 };
const subSx = { ...metaAdsMutedTextSx, fontSize: '12px', lineHeight: '16px' };
const oneLineSx = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
// Todas las filas (menos «Acciones en Meta») tienen el mismo alto: título + subtítulo de una línea.
// Sin subtítulo, el título va centrado en ese alto.
const ROW_CONTENT_MIN_HEIGHT = '34px';
const contentSx = { minWidth: 0, minHeight: ROW_CONTENT_MIN_HEIGHT };
const singleLineContentSx = { ...contentSx, display: 'flex', alignItems: 'center', columnGap: 0.5 };
const singleLineRowSx = { ...rowSx, alignItems: 'center' };
const singleLineIconSx = { ...rowIconSx, mt: 0 };
const infoButtonSx = {
  p: '2px', color: colors.textSecondary, flexShrink: 0,
  '&:hover, &:focus-visible': { color: colors.primaryLight, bgcolor: 'transparent' },
};
const actionSx = { textTransform: 'none', fontWeight: 600, px: 0.75, py: 0, minWidth: 0, fontSize: '12.5px', color: colors.primaryLight };

/** «Seguís 1 de 3 cuentas» (o «Seguís 2 cuentas» si sigue todas / no sabemos las disponibles). */
export function metaTrackedAccountsText(connection) {
  const tracked = connection?.adAccounts?.trackedCount || 0;
  const available = connection?.adAccounts?.availableCount || 0;
  if (!tracked) return 'Todavía no seguís ninguna cuenta';
  if (available > tracked) return `Seguís ${tracked} de ${plural(available, 'cuenta', 'cuentas')}`;
  return `Seguís ${plural(tracked, 'cuenta', 'cuentas')}`;
}

/** «7 campañas» + «3 activas · 4 inactivas» (de `metaCampaignMix`). */
export function metaCampaignMixText(mix) {
  if (!mix) return null;
  if (mix.total == null) return { title: plural(mix.active, 'campaña activa', 'campañas activas'), detail: null };
  if (!mix.total) return { title: 'No hay campañas en las cuentas que seguís', detail: null };
  return {
    title: plural(mix.total, 'campaña', 'campañas'),
    detail: `${plural(mix.active, 'activa', 'activas')} · ${plural(mix.inactive, 'inactiva', 'inactivas')}`,
  };
}

/**
 * ⚙ «Configuración de Meta» (junto a la frescura, en el header fijo de Campañas):
 * Popover anclado (Esc / clic afuera cierra) con la conexión, las cuentas
 * seguidas («Cambiar» abre la vista de cuentas), el total de campañas y, al
 * final y discreto, «Desconectar Meta» (la confirmación la pone el panel). Las cuentas
 * van en el tooltip del ⓘ; «Historial de cambios» es una fila propia (solo el dueño).
 * Reemplaza las tarjetas grandes del inicio conectado.
 *
 * @param {{ connection: object, trackedAccounts?: object[]|null, accountsLoading?: boolean,
 *   campaignMix?: object|null, canManage?: boolean, onOpenAccounts: () => void,
 *   onDisconnect?: (() => void)|null, disconnecting?: boolean, onOpen?: (() => void)|null }} props
 */
export default function MetaSettingsButton({
  connection,
  trackedAccounts = null,
  accountsLoading = false,
  campaignMix = null,
  canManage = false,
  onOpenAccounts,
  onDisconnect = null,
  disconnecting = false,
  onOpen = null,
  // Header del panel (a la izquierda de la ✕): estilo de los íconos del header de Registros.
  inPanelHeader = false,
  // F2a: fila «Acciones en Meta» (antes de «Desconectar Meta»); null = ⚙ como antes.
  writeRow = null,
  // F2a: abrir ⚙ desde afuera (tooltip «Habilitá… en Configuración de Meta», vuelta del login). Cambia → abre.
  openRequest = 0,
  // F2b (solo el dueño): fila «Historial de cambios», después de «Acciones en Meta». null = sin fila.
  onOpenLog = null,
}) {
  const [anchor, setAnchor] = useState(null);
  const buttonRef = useRef(null);
  useEffect(() => {
    if (!openRequest || !buttonRef.current) return;
    setAnchor(buttonRef.current);
    if (onOpen) onOpen();
    // Solo cuando cambia el pedido.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openRequest]);
  const open = Boolean(anchor);
  const close = () => setAnchor(null);
  const syncLabel = formatMetaSyncTime(connection?.lastSyncAt);
  const healthy = connection?.connected && !connection?.needsReconnect && connection?.lastSyncStatus !== 'error';
  const accountsNames = (trackedAccounts || [])
    .map((a) => (a.currency ? `${a.name} (${a.currency})` : a.name));
  const campaigns = metaCampaignMixText(campaignMix);

  return (
    <>
      <Tooltip title={<span>{META_SETTINGS_LABEL}</span>} placement={inPanelHeader ? 'bottom' : 'top'} arrow>
        <IconButton
          ref={buttonRef}
          size="small"
          aria-label={META_SETTINGS_LABEL}
          aria-haspopup="dialog"
          aria-expanded={open ? 'true' : undefined}
          onClick={(e) => {
            setAnchor(e.currentTarget);
            if (onOpen) onOpen();
          }}
          data-testid="meta-settings-button"
          sx={{
            ...(inPanelHeader ? metaAdsPanelHeaderIconSx : metaAdsIconButtonSx),
            color: open ? colors.primaryLight : (inPanelHeader ? '#E8F4F4' : colors.textSecondary),
            bgcolor: open ? 'rgba(72,146,145,0.22)' : undefined,
          }}
        >
          <SettingsOutlinedIcon sx={{ fontSize: inPanelHeader ? 20 : 17 }} />
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            role: 'dialog',
            'aria-label': META_SETTINGS_LABEL,
            'data-testid': 'meta-settings-popover',
            sx: {
              mt: 0.75,
              width: 'min(340px, 92vw)',
              bgcolor: '#2A2A2A',
              backgroundImage: 'none',
              color: colors.textPrimary,
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: '12px',
              boxShadow: '0 12px 32px rgba(0,0,0,0.6)',
              px: 2,
              pt: 1.5,
              pb: 1,
            },
          },
        }}
      >
        <Typography
          component="h3"
          sx={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: colors.textPanelTitle, pb: 0.75 }}
        >
          {META_SETTINGS_LABEL}
        </Typography>

        <Box sx={rowSx} data-testid="meta-settings-connection">
          {healthy
            ? <CheckCircleIcon sx={{ ...rowIconSx, color: colors.primaryLight }} aria-hidden />
            : <ErrorOutlineIcon sx={{ ...rowIconSx, color: colors.warning }} aria-hidden />}
          <Box sx={contentSx}>
            <Typography sx={{ ...lineSx, ...oneLineSx }}>{healthy ? 'Meta conectado' : 'Meta conectado, con problemas'}</Typography>
            <Typography sx={{ ...subSx, ...oneLineSx }}>{syncLabel ? `Última actualización: ${syncLabel}` : 'Todavía sin actualizar'}</Typography>
          </Box>
          <span />
        </Box>

        <Box sx={singleLineRowSx} data-testid="meta-settings-accounts">
          <AccountBalanceWalletOutlinedIcon sx={singleLineIconSx} aria-hidden />
          <Box sx={singleLineContentSx}>
            <Typography sx={{ ...lineSx, ...oneLineSx }}>{metaTrackedAccountsText(connection)}</Typography>
            {accountsLoading && !accountsNames.length ? (
              <Skeleton variant="circular" width={14} height={14} sx={{ ...metaAdsSkeletonSx, flexShrink: 0 }} />
            ) : accountsNames.length ? (
              <Tooltip
                title={(
                  <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }} data-testid="meta-settings-accounts-names">
                    {accountsNames.map((n) => <li key={n}>{n}</li>)}
                  </Box>
                )}
                placement="bottom"
                arrow
                enterTouchDelay={0}
              >
                <IconButton size="small" aria-label="Ver cuentas" sx={infoButtonSx} data-testid="meta-settings-accounts-info">
                  <InfoOutlinedIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            ) : null}
          </Box>
          <Button
            size="small"
            onClick={() => { close(); onOpenAccounts(); }}
            sx={actionSx}
            data-testid="meta-settings-accounts-action"
          >
            {canManage ? 'Cambiar' : 'Ver'}
          </Button>
        </Box>

        <Box sx={rowSx} data-testid="meta-settings-campaigns">
          <CampaignOutlinedIcon sx={rowIconSx} aria-hidden />
          <Box sx={contentSx}>
            {campaigns ? (
              <>
                <Typography sx={{ ...lineSx, ...oneLineSx }}>{campaigns.title}</Typography>
                {campaigns.detail ? <Typography sx={{ ...subSx, ...oneLineSx }}>{campaigns.detail}</Typography> : null}
              </>
            ) : (
              <Skeleton variant="rounded" width="60%" height={16} sx={metaAdsSkeletonSx} />
            )}
          </Box>
          <span />
        </Box>

        {writeRow}

        {onOpenLog ? (
          <ButtonBase
            onClick={() => { close(); onOpenLog(); }}
            data-testid="meta-settings-log"
            sx={{
              ...singleLineRowSx, width: '100%', textAlign: 'left', justifyContent: 'stretch', mx: -1, px: 1,
              boxSizing: 'content-box', borderRadius: '6px',
              '&:hover, &.Mui-focusVisible': { bgcolor: 'rgba(255,255,255,0.04)' },
            }}
          >
            <HistoryOutlinedIcon sx={singleLineIconSx} aria-hidden />
            <Box sx={singleLineContentSx}>
              <Typography component="span" sx={{ ...lineSx, ...oneLineSx }}>Historial de cambios</Typography>
            </Box>
            <ChevronRightIcon sx={{ fontSize: 18, color: colors.textSecondary }} aria-hidden />
          </ButtonBase>
        ) : null}

        {onDisconnect ? (
          <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.06)', pt: 0.75 }}>
            <Button
              size="small"
              onClick={onDisconnect}
              disabled={disconnecting}
              data-testid="meta-ads-disconnect"
              sx={{ ...metaAdsQuietButtonSx, px: 0.5 }}
            >
              {disconnecting ? 'Desconectando…' : 'Desconectar Meta'}
            </Button>
          </Box>
        ) : null}
      </Popover>
    </>
  );
}
